import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LuMail,
  LuInbox,
  LuUsers,
  LuArrowUpRight,
  LuRefreshCw,
  LuDownload,
  LuSearch,
  LuGlobe,
  LuLoader,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { Card, Button, Tooltip } from "@destroy/ui";
import { api, type AdminInboxAnalytics } from "@/lib/api";
import { PageHeader } from "@/components/ui/page-header";

function getLast7Days(): string[] {
  const out: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    out.push(d.toLocaleDateString("en-US", { weekday: "short" }));
  }
  return out;
}

function mapToWeek(activity: Array<{ date: string; count: number }>): number[] {
  const result: number[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().split("T")[0];
    result.push(activity.find((a) => a.date === iso)?.count ?? 0);
  }
  return result;
}

const TIER_COLOURS: Record<string, string> = {
  anonymous: "bg-[var(--color-text-subtle)]",
  free: "bg-[var(--color-text-muted)]",
  pro: "bg-[var(--color-accent-light)]",
  business: "bg-[#41d1ff]",
  enterprise: "bg-[#d8b4fe]",
};

const TIER_BADGE: Record<string, string> = {
  anonymous: "text-[var(--color-text-subtle)] bg-[var(--color-elevated)] border-[var(--color-border)]",
  free: "text-[var(--color-text-muted)] bg-[var(--color-elevated)] border-[var(--color-border)]",
  pro: "text-[var(--color-accent-light)] bg-[rgba(139,92,246,0.12)] border-[rgba(139,92,246,0.3)]",
  business: "text-[#41d1ff] bg-[rgba(65,209,255,0.1)] border-[rgba(65,209,255,0.3)]",
  enterprise: "text-[#d8b4fe] bg-[rgba(189,52,254,0.1)] border-[rgba(189,52,254,0.3)]",
};

export function InboxesPage() {
  const [search, setSearch] = useState("");

  const analytics = useQuery({
    queryKey: ["console", "admin", "inboxes"],
    queryFn: () => api.adminInboxes(),
  });

  const data = analytics.data;

  const days = useMemo(() => getLast7Days(), []);
  const inboxesData = data ? mapToWeek(data.activity.inboxesCreated) : [];
  const messagesData = data ? mapToWeek(data.activity.messagesReceived) : [];

  const currentTotal = data?.overview.current ?? 0;
  const anonymousPct = currentTotal > 0
    ? ((data!.byOwnerType.anonymous / currentTotal) * 100).toFixed(1)
    : "0";
  const forwardingPct = currentTotal > 0
    ? ((data!.overview.withForwarding / currentTotal) * 100).toFixed(1)
    : "0";

  function isExpired(ts: string) {
    return new Date(ts) <= new Date();
  }

  function exportCsv() {
    if (!data) return;
    const rows: Array<Array<string | number>> = [
      ["Inbox Analytics Export"],
      [`Generated: ${new Date().toISOString()}`],
      [],
      ["Overview"],
      ["Metric", "Value"],
      ["Total inboxes (lifetime)", data.overview.total],
      ["Current", data.overview.current],
      ["Active", data.overview.active],
      ["Expired", data.overview.expired],
      ["With forwarding", data.overview.withForwarding],
      ["Total messages (lifetime)", data.overview.totalMessages],
      ["Current messages", data.overview.currentMessages],
      [],
      ["Owner type"],
      ["Anonymous", data.byOwnerType.anonymous],
      ["Authenticated", data.byOwnerType.authenticated],
      [],
      ["Tier"],
      ...Object.entries(data.byTier).map(([t, n]) => [t, n] as [string, number]),
      [],
      ["Domain"],
      ...data.byDomain.map((d) => [d.domain, d.count] as [string, number]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inbox-analytics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const searchLower = search.toLowerCase();
  const filterRows = <T extends { address: string; ownerEmail: string | null }>(rows: T[]) =>
    rows.filter(
      (r) =>
        r.address.toLowerCase().includes(searchLower) ||
        (r.ownerEmail?.toLowerCase().includes(searchLower) ?? false),
    );

  return (
    <>
      <PageHeader
        icon={LuMail}
        title="Inbox analytics"
        description="Lifetime totals, distribution, activity, and per-inbox breakdowns."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => analytics.refetch()}
              disabled={analytics.isFetching}
            >
              <LuRefreshCw className={`w-4 h-4 ${analytics.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="secondary" onClick={exportCsv} disabled={!data}>
              <LuDownload className="w-4 h-4" />
              Export
            </Button>
          </>
        }
      />

      <div className="p-8 space-y-8">
        {analytics.isLoading && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <LuLoader className="w-4 h-4 animate-spin" /> Loading inbox analytics...
          </div>
        )}

        {data && (
          <>
            {/* Overview */}
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mb-3">
                Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  icon={LuInbox}
                  label="Inboxes (lifetime)"
                  value={data.overview.total.toLocaleString()}
                  subValue={`${data.overview.current.toLocaleString()} current · ${data.overview.active.toLocaleString()} active`}
                />
                <StatCard
                  icon={LuMail}
                  label="Messages (lifetime)"
                  value={data.overview.totalMessages.toLocaleString()}
                  subValue={`${data.overview.currentMessages.toLocaleString()} current`}
                />
                <StatCard
                  icon={LuUsers}
                  label="Anonymous inboxes"
                  value={data.byOwnerType.anonymous.toLocaleString()}
                  subValue={`${anonymousPct}% of current`}
                />
                <StatCard
                  icon={LuArrowUpRight}
                  label="Forwarding enabled"
                  value={data.overview.withForwarding.toLocaleString()}
                  subValue={`${forwardingPct}% of current`}
                />
              </div>
            </section>

            {/* Activity */}
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mb-3">
                Activity
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BarChart
                  data={inboxesData}
                  labels={days}
                  title="Inboxes created"
                  color="#41d1ff"
                />
                <BarChart
                  data={messagesData}
                  labels={days}
                  title="Messages received"
                  color="var(--color-accent-light)"
                />
              </div>
            </section>

            {/* Distribution */}
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mb-3">
                Distribution
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card padding="md">
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-4">By source</h4>
                  <DistributionList
                    items={Object.entries(data.bySource).map(([k, v]) => ({
                      key: k,
                      label: { api: "API", web: "Web", anonymous: "Anonymous", extension: "Extension" }[k] ?? k,
                      count: v,
                      total: currentTotal,
                      colour:
                        k === "api"
                          ? "bg-[var(--color-accent-light)]"
                          : k === "web"
                            ? "bg-[#41d1ff]"
                            : k === "extension"
                              ? "bg-[#fcd34d]"
                              : "bg-[var(--color-text-subtle)]",
                    }))}
                  />
                </Card>

                <Card padding="md">
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-4">By owner tier</h4>
                  <DistributionList
                    items={Object.entries(data.byTier).map(([tier, count]) => ({
                      key: tier,
                      label: tier,
                      count,
                      total: currentTotal,
                      colour: TIER_COLOURS[tier] ?? TIER_COLOURS.free,
                      capitalize: true,
                    }))}
                  />
                </Card>

                <Card padding="md">
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-4">By domain</h4>
                  <div className="space-y-3">
                    {data.byDomain.slice(0, 6).map((d) => {
                      const pct = currentTotal > 0 ? (d.count / currentTotal) * 100 : 0;
                      return (
                        <div key={d.domain} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <LuGlobe className="w-3.5 h-3.5 text-[var(--color-text-muted)] shrink-0" />
                            <span className="text-sm text-[var(--color-text)] font-mono truncate">
                              {d.domain}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-[var(--color-text)]">
                              {d.count.toLocaleString()}
                            </span>
                            <span className="text-[10px] text-[var(--color-text-subtle)] tabular-nums">
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {data.byDomain.length === 0 && (
                      <p className="text-sm text-[var(--color-text-muted)]">No data</p>
                    )}
                  </div>
                </Card>
              </div>
            </section>

            {/* Tables */}
            <section>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)]">
                  Inbox tables
                </h2>
                <div className="relative w-full sm:w-72">
                  <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
                  <input
                    type="search"
                    placeholder="Search address or owner..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <InboxTable
                  title="Top inboxes by message count"
                  rows={filterRows(data.topInboxes)}
                  isExpired={isExpired}
                  trailing={(i) => (
                    <span className="font-medium text-[var(--color-text)] tabular-nums">
                      {i.messageCount.toLocaleString()}
                    </span>
                  )}
                  trailingLabel="Messages"
                  emptyMessage={search ? "No matching inboxes" : "No inboxes yet"}
                />

                <InboxTable
                  title={`Active inboxes (${data.activeInboxes.length})`}
                  rows={filterRows(data.activeInboxes)}
                  isExpired={isExpired}
                  trailing={(i) => {
                    const ms = new Date(i.expiresAt).getTime() - Date.now();
                    const mins = Math.floor(ms / 60000);
                    const hours = Math.floor(mins / 60);
                    const remMins = mins % 60;
                    const left = hours > 0 ? `${hours}h ${remMins}m` : `${mins}m`;
                    const soon = mins <= 5;
                    return (
                      <span
                        className={`font-mono text-xs tabular-nums ${
                          soon ? "text-[var(--color-warning)]" : "text-[var(--color-text-muted)]"
                        }`}
                      >
                        {left}
                      </span>
                    );
                  }}
                  trailingLabel="Expires"
                  emptyMessage={search ? "No matching inboxes" : "No active inboxes"}
                />
              </div>
            </section>

            {/* Recent */}
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mb-3">
                Recent inboxes
                <span className="ml-2 text-[10px] normal-case tracking-normal text-[var(--color-text-muted)]">
                  (last 15 created)
                </span>
              </h2>
              <Card padding="md">
                <div className="max-h-80 overflow-y-auto -mx-6">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-[var(--color-surface)]">
                      <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                        <th className="px-6 pb-2 font-medium">Address</th>
                        <th className="px-6 pb-2 font-medium">Owner</th>
                        <th className="px-6 pb-2 font-medium text-right">Messages</th>
                        <th className="px-6 pb-2 font-medium text-right">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterRows(data.recentInboxes).map((i) => (
                        <tr
                          key={i.id}
                          className="border-b border-[var(--color-border)] last:border-0"
                        >
                          <td className="px-6 py-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                  isExpired(i.expiresAt)
                                    ? "bg-[var(--color-error)]"
                                    : "bg-[#6ee7b7]"
                                }`}
                              />
                              <span className="font-mono text-xs text-[var(--color-text)] truncate max-w-[220px]">
                                {i.address}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-2">
                            <OwnerCell email={i.ownerEmail} tier={i.ownerTier} />
                          </td>
                          <td className="px-6 py-2 text-right text-[var(--color-text)] tabular-nums">
                            {i.messageCount}
                          </td>
                          <td className="px-6 py-2 text-right text-[var(--color-text-muted)] font-mono tabular-nums text-xs">
                            {new Date(i.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {filterRows(data.recentInboxes).length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-4 text-center text-[var(--color-text-muted)]">
                            {search ? "No matching inboxes" : "No inboxes yet"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>
          </>
        )}
      </div>
    </>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subValue?: string;
}) {
  return (
    <Card padding="md">
      <div className="flex items-start justify-between mb-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
          {label}
        </p>
        <div className="w-7 h-7 rounded-md bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.25)] flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-[var(--color-accent-light)]" />
        </div>
      </div>
      <p className="text-2xl font-semibold text-[var(--color-text)] tracking-tight tabular-nums">
        {value}
      </p>
      {subValue && (
        <p className="text-[11px] text-[var(--color-text-subtle)] mt-1.5">{subValue}</p>
      )}
    </Card>
  );
}

function BarChart({
  data,
  labels,
  title,
  color,
}: {
  data: number[];
  labels: string[];
  title: string;
  color: string;
}) {
  const max = Math.max(...data, 1);
  const total = data.reduce((a, b) => a + b, 0);
  const avg = (total / 7).toFixed(1);
  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-[var(--color-text)]">{title}</h4>
        <span className="text-xs text-[var(--color-text-subtle)]">Last 7 days</span>
      </div>
      <div className="flex items-end justify-between gap-2 h-24">
        {data.map((v, i) => {
          const h = Math.max((v / max) * 64, 3);
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full flex items-end justify-center h-16">
                <Tooltip content={`${labels[i]}: ${v.toLocaleString()}`} position="top">
                  <div
                    className="w-6 rounded-t transition-all"
                    style={{
                      height: `${h}px`,
                      backgroundColor: v > 0 ? color : "var(--color-border)",
                      opacity: v > 0 ? 0.85 : 0.4,
                    }}
                  />
                </Tooltip>
              </div>
              <span className="text-[10px] text-[var(--color-text-subtle)]">{labels[i]}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-3 pt-3 border-t border-[var(--color-border)]">
        <span className="text-xs text-[var(--color-text-muted)]">
          Total: <span className="font-medium text-[var(--color-text)]">{total.toLocaleString()}</span>
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          Avg: <span className="font-medium text-[var(--color-text)]">{avg}</span>/day
        </span>
      </div>
    </Card>
  );
}

function DistributionList({
  items,
}: {
  items: Array<{
    key: string;
    label: string;
    count: number;
    total: number;
    colour: string;
    capitalize?: boolean;
  }>;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-[var(--color-text-muted)]">No data</p>;
  }
  return (
    <div className="space-y-3">
      {items.map((it) => {
        const pct = it.total > 0 ? (it.count / it.total) * 100 : 0;
        return (
          <div key={it.key} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${it.colour}`} />
              <span
                className={`text-sm text-[var(--color-text)] ${it.capitalize ? "capitalize" : ""}`}
              >
                {it.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--color-text)] tabular-nums">
                {it.count.toLocaleString()}
              </span>
              <span className="text-[10px] text-[var(--color-text-subtle)] tabular-nums">
                {pct.toFixed(1)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OwnerCell({ email, tier }: { email: string | null; tier: string | null }) {
  const t = tier ?? "anonymous";
  const cls = TIER_BADGE[t] ?? TIER_BADGE.anonymous;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[var(--color-text-muted)] truncate max-w-[160px]" title={email ?? "Anonymous"}>
        {email ?? "Anonymous"}
      </span>
      <span className={`inline-block w-fit px-1.5 py-0.5 text-[10px] font-medium rounded capitalize border ${cls}`}>
        {t}
      </span>
    </div>
  );
}

interface InboxRow {
  id: string;
  address: string;
  ownerEmail: string | null;
  ownerTier: string | null;
  expiresAt: string;
  createdAt: string;
  messageCount: number;
}

function InboxTable({
  title,
  rows,
  isExpired,
  trailing,
  trailingLabel,
  emptyMessage,
}: {
  title: string;
  rows: InboxRow[];
  isExpired: (ts: string) => boolean;
  trailing: (row: InboxRow) => React.ReactNode;
  trailingLabel: string;
  emptyMessage: string;
}) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-3">
        {title}
      </h3>
      <Card padding="md">
        <div className="max-h-80 overflow-y-auto -mx-6">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-[var(--color-surface)]">
              <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                <th className="px-6 pb-2 font-medium">Address</th>
                <th className="px-6 pb-2 font-medium">Owner</th>
                <th className="px-6 pb-2 font-medium text-right">{trailingLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="px-6 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          isExpired(i.expiresAt)
                            ? "bg-[var(--color-error)]"
                            : "bg-[#6ee7b7]"
                        }`}
                      />
                      <span
                        className="font-mono text-xs text-[var(--color-text)] truncate max-w-[160px]"
                        title={i.address}
                      >
                        {i.address}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-2">
                    <OwnerCell email={i.ownerEmail} tier={i.ownerTier} />
                  </td>
                  <td className="px-6 py-2 text-right">{trailing(i)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-[var(--color-text-muted)]">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
